/**
 * useGetStartedChecklist Hook
 *
 * Main hook for the progressive onboarding checklist.
 * Reads completion state from company.payment flags, provides real-time updates,
 * and exposes mutations to mark the checklist as triggered/completed.
 */

import { useMemo, useCallback, useRef, useEffect } from 'react';
import { useMutation, useApolloClient } from '@apollo/client/react';
import { UPDATE_GET_STARTED, GET_COMPANY_INFO } from '@scio3/graphql-client';
import { analyticsGetStartedStep, analyticsGetStartedCompleted } from '@/lib/analytics-service';
import type { UpdateGetStartedResponse } from '@scio3/graphql-client';
import type { CompanyPayment, CompanyInfo } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { CHECKLIST_STEPS, STEP_FLAG_MAPPING, isFlagCompleted } from '../constants';
import { useGetStartedWidgetStore } from '../stores/get-started-widget-store';
import type { ChecklistState, StepState } from '../types';
import { StepId } from '../types';

/**
 * Derive step completion status from payment flags.
 * A step is complete when ANY of its mapped flags is "triggered" — flipping
 * a single flag in Mongo cleans the whole step.
 */
function isStepCompleted(stepId: StepId, payment: CompanyPayment | undefined): boolean {
  if (!payment) return false;

  const flagNames = STEP_FLAG_MAPPING[stepId];
  return flagNames.some((flagName) => {
    const value = payment[flagName] as string | undefined;
    return isFlagCompleted(value);
  });
}

/**
 * Hook for the Get Started checklist widget.
 * Returns current state, step data, and mutation handlers.
 */
export function useGetStartedChecklist() {
  const company = useAuthStore((s) => s.company);
  const setCompany = useAuthStore((s) => s.setCompany);
  const client = useApolloClient();
  // Widget visibility lives in a dedicated Zustand store so the bottom-right
  // panel and the top-bar restore pill share one source of truth (and the
  // user's chosen state persists across reloads).
  // "Get your screen set up" completes on this client-side flag, not a
  // backend payment flag (see STEP_FLAG_MAPPING).
  const screenSetupDownloadClicked = useGetStartedWidgetStore((s) => s.screenSetupDownloadClicked);

  // Mutation to update get-started state
  const [updateGetStarted, { loading: updateLoading }] =
    useMutation<UpdateGetStartedResponse>(UPDATE_GET_STARTED);

  // Refetch company info (for real-time updates)
  const refetchCompany = useCallback(async () => {
    try {
      const result = await client.query<{ getCompanyInfo: CompanyInfo }>({
        query: GET_COMPANY_INFO,
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      });
      if (result.data?.getCompanyInfo) {
        setCompany(result.data.getCompanyInfo);
      }
    } catch {
      // Silently fail — company info is not critical for app function
    }
  }, [client, setCompany]);

  // Subscribe to real-time company updates
  // The subscription filters by 'company' entity type
  useRealtimeSubscription('company', refetchCompany, {
    refetchOnUpdate: true, // We need to refetch on UPDATE events for company flags
  });

  // Track previously completed step IDs to detect new completions
  const prevCompletedRef = useRef<Set<StepId>>(new Set());
  // Guards the analytics effect below. Until the first render where `company`
  // data is loaded, we can't tell a genuine new completion apart from a step
  // the user finished in a previous session — so the baseline is seeded
  // silently on that first render and events fire only for transitions after.
  const baselineSeededRef = useRef(false);

  // Compute checklist state from company data
  const state = useMemo((): ChecklistState => {
    const payment = company?.payment;

    // Preconfigured screens (Mass Add Screens) count as "screen set up" too.
    // `preConfigureDevices` creates real device rows and bumps `company.noOfScreens`
    // — the monotonic "Screen N" naming counter — but sets NO pairing timestamp, so
    // `payment.firstDevicePaired` stays empty. Without this an account that only ever
    // preconfigures had step 1 sitting unchecked forever while its screens were listed
    // right there. Same signal pair ScreensPage uses for `showToolbarControls`
    // (ScreensPage.tsx) and OnboardingBackdropToolbar; keep the three in step.
    //
    // Deliberately step 1 ONLY: step 2 ("Pair screen and content") still requires
    // `firstScreenAssignmentEventFired`, because preconfiguring a screen does not put
    // content on it.
    const hasPreconfiguredScreens = (company?.noOfScreens ?? 0) > 0;

    // Build steps with completion status
    const steps: StepState[] = CHECKLIST_STEPS.map((step) => ({
      ...step,
      completed:
        isStepCompleted(step.id, payment) ||
        (step.id === StepId.GET_SCREEN_SETUP &&
          (screenSetupDownloadClicked || hasPreconfiguredScreens)),
    }));

    const completedCount = steps.filter((s) => s.completed).length;
    const totalSteps = steps.length;
    const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

    // Check if all required steps are completed
    const requiredSteps = steps.filter((s) => s.required);
    const allRequiredComplete = requiredSteps.every((s) => s.completed);

    // Visible until the checklist is dismissed/completed. NOT gated on
    // widgetState: a COLLAPSED widget is still occupying the bottom-right
    // corner, and this flag is what the referral launcher reads (via
    // optibot-store.checklistOpen) to decide whether the corner is free. If
    // collapsed counted as hidden, the referral pill would appear on top of
    // the collapsed checklist.
    const isVisible = !company?.getStarted?.completed;

    return {
      isComplete: allRequiredComplete,
      isVisible,
      steps,
      completedCount,
      totalSteps,
      progressPercent,
    };
  }, [company, screenSetupDownloadClicked]);

  // Track step completions via PostHog (side effect, not inside useMemo).
  // The first render where company data is loaded seeds the baseline WITHOUT
  // firing any event — steps already complete at that point were finished in a
  // previous session. Only genuine incomplete→complete transitions after the
  // baseline is seeded emit events, so returning users don't re-fire stale
  // completions on every page load.
  useEffect(() => {
    const completedIds = new Set(state.steps.filter((s) => s.completed).map((s) => s.id));

    if (!baselineSeededRef.current) {
      if (!company) return; // company not loaded yet — wait for real data
      baselineSeededRef.current = true;
      prevCompletedRef.current = completedIds;
      return;
    }

    const prevCompleted = prevCompletedRef.current;
    for (const step of state.steps) {
      if (step.completed && !prevCompleted.has(step.id)) {
        const stepIndex = CHECKLIST_STEPS.findIndex((s) => s.id === step.id);
        analyticsGetStartedStep({ stepId: step.id, stepNumber: stepIndex + 1 });
      }
    }
    if (state.isComplete && completedIds.size > prevCompleted.size) {
      analyticsGetStartedCompleted({ stepsCompleted: state.completedCount });
    }
    prevCompletedRef.current = completedIds;
  }, [state, company]);

  // Mark checklist as completed (hides widget permanently)
  const completeChecklist = useCallback(async () => {
    // Optimistically hide widget immediately for responsive UX
    if (company) {
      setCompany({
        ...company,
        getStarted: {
          triggered: company.getStarted?.triggered ?? true,
          completed: true,
          completedDate: new Date().toISOString(),
        },
      });
    }
    try {
      await updateGetStarted({
        variables: {
          updateData: { getStarted: { completed: true } },
        },
      });
      // Refetch to sync server state on success
      await refetchCompany();
    } catch (err) {
      console.error('Failed to complete checklist:', err);
      // Keep optimistic state — widget stays hidden even if API fails
    }
  }, [updateGetStarted, refetchCompany, company, setCompany]);

  // Trigger the checklist (makes widget visible)
  const triggerChecklist = useCallback(async () => {
    try {
      await updateGetStarted({
        variables: {
          updateData: { getStarted: { triggered: true } },
        },
      });
      await refetchCompany();
    } catch (err) {
      console.error('Failed to trigger checklist:', err);
    }
  }, [updateGetStarted, refetchCompany]);

  return {
    /** Current checklist state */
    state,
    /** Whether a mutation is in progress */
    loading: updateLoading,
    /** Mark checklist as completed (hides widget permanently) */
    completeChecklist,
    /** Trigger the checklist (makes widget visible) */
    triggerChecklist,
    /** Manually refetch company data */
    refetch: refetchCompany,
  };
}
